<script setup lang="ts">
import { Button, Modal } from "@frontiers-vue3/public-pages";
import type { IArticleTypes, IResearchTopic, IJournal } from "~/types/index";

const { currentResearchTopic } = useMain();
const terminology = useTerminologyStore();

const researchTopic = computed<IResearchTopic>(() => currentResearchTopic);
const open = ref(false);
const toggleModal = () => {
  open.value = !open.value;
};

const articleTypesList = computed(() => {
  if (!researchTopic.value.journals) return [];

  const onlineJournals = researchTopic.value.journals.filter(
    (j: IJournal) => j.isOnline
  );

  const allArticleTypes: IArticleTypes[] = onlineJournals.flatMap(
    (journal: IJournal) => [
      ...(journal.articleTypes || []),
      ...(journal.sections
        ?.filter((s) => s.isOnline)
        .flatMap((section) => section.articleTypes || []) || [])
    ]
  );

  const notDisplayedArticleTypes = [
    "Retraction",
    "Correction",
    "Addendum",
    "Special Issue Editorial"
  ];
  const articleTypes = allArticleTypes
    .filter(
      (articleType: IArticleTypes) =>
        !notDisplayedArticleTypes.includes(articleType.name)
    )
    .map((articleType: IArticleTypes) => articleType.name);

  const sortWithLowecase = (a: string, b: string) => {
    const str1 = a.toLowerCase();
    const str2 = b.toLowerCase();

    if (str1 > str2) return 1;
    if (str1 < str2) return -1;
    return 0;
  };

  return [...new Set(articleTypes.sort(sortWithLowecase))];
});

const articleTypeTerminology = computed(() => {
  return terminology.getTerm("article");
});

const articlesTypeTerminology = computed(() => {
  return terminology.getTerm("articles");
});

const researchTopicTerminology = computed(() => {
  return terminology.getTerm("researchTopic");
});

const authorsTerminology = computed(() => {
  return terminology.getTerm("authors");
});

const maxArticleTypesToDisplay = 100;

const articleTypesForDisplay = computed(() => {
  return articleTypesList.value.slice(0, maxArticleTypesToDisplay);
});
const isModalEnabled = computed(() => {
  return articleTypesList.value.length > maxArticleTypesToDisplay;
});
</script>
<template>
  <section
    v-if="articleTypesList.length"
    id="ArticleTypesSection"
    class="Overview__articleTypes">
    <h3 class="Overview__title BlockTitle">
      {{ articleTypeTerminology }} types and fees
    </h3>
    <p>
      This
      {{ researchTopicTerminology }}
      accepts the following article types, unless otherwise specified in the
      {{ researchTopicTerminology }} description:
    </p>
    <ul>
      <li
        v-for="(articleType, index) in articleTypesForDisplay"
        :key="`${index}-${articleType}`">
        {{ articleType }}
      </li>
      <li v-if="isModalEnabled">
        <Button
          text="... View all formats"
          type="linkButton"
          size="Medium"
          @click="toggleModal" />
      </li>
    </ul>
    <p>
      {{ articlesTypeTerminology }} that are accepted for publication by our
      external editors following rigorous peer review incur a publishing fee
      charged to {{ authorsTerminology }}, institutions, or funders.
    </p>
  </section>
  <Modal
    :force-show="open"
    :title="`${articleTypeTerminology} types`"
    heading="p">
    <template #body>
      <p>
        This
        {{ researchTopicTerminology }}
        accepts the following article types, unless otherwise specified in the
        {{ researchTopicTerminology }} description:
      </p>
      <ul>
        <li
          v-for="(articleType, index) in articleTypesList"
          :key="`${index}-${articleType}`">
          {{ articleType }}
        </li>
      </ul>
    </template>
  </Modal>
</template>
